import { periodenkuerzel } from './sprache'

/**
 * Zwischensummen im Periodenbild (7.3 Abschnitt 26, R1018).
 *
 * **Der Auftrag vom 2026-10-04:** *„Ich möchte eine Anzeigeoption, dass eine
 * Zwischensumme eingeblendet werden kann. Im Periodenaufriss nach Quartal,
 * Halbjahr, Jahr, Ausgewählter Zeit (wenn ich als Anwender z. B. 8 Monate
 * ausgewählt habe und deren Werte summiert in einer Spalte neben die
 * Einzelmonate sehen möchte)."*
 *
 * **Summiert wird, was sich summieren lässt.** Ein Monatswert, eine
 * Verkehrszahl, ein Zahlungsstrom: ja. Ein Bestand nicht — der Fonds am Anfang
 * eines Quartals ist der des ersten Monats, der am Ende der des letzten. Und
 * wo die Spalten selbst Stände zeigen (Saldo in der Bilanz, *Kumuliert seit
 * Jahresbeginn*), stünde in der Zwischensumme nur die letzte Spalte noch
 * einmal; dort gibt es keine.
 */

export type Zwischensummenart = 'quartal' | 'halbjahr' | 'jahr' | 'zeitraum'

/** In dieser Reihenfolge stehen sie hinter ihrer letzten Spalte. */
export const ZWISCHENSUMMENARTEN: Zwischensummenart[] = ['quartal', 'halbjahr', 'jahr', 'zeitraum']

/** Eine Zwischensumme über die Spalten `von` bis `bis` (je einschließlich). */
export type Zwischenspalte = { art: Zwischensummenart; von: number; bis: number }

function gruppe(art: Zwischensummenart, periode: number): string {
  const jahr = Math.floor(periode / 100)
  const monat = periode % 100
  switch (art) {
    case 'quartal':
      return `${jahr}-Q${Math.ceil(monat / 3)}`
    case 'halbjahr':
      return `${jahr}-H${monat <= 6 ? 1 : 2}`
    case 'jahr':
      return String(jahr)
    case 'zeitraum':
      return 'alle'
  }
}

/**
 * Welche Zwischensummen hinter welchen Spalten stehen.
 *
 * **Eine Zwischensumme über eine einzige Spalte entfällt** — sie wiederholte
 * die Spalte. **Und zwei über dieselben Spalten stehen einmal da:** Wer nur
 * Januar bis März gewählt hat, bekäme sonst Quartal, Halbjahr, Jahr und
 * Zeitraum als vier gleiche Spalten.
 */
export function zwischenspalten(
  perioden: number[],
  gewaehlt: ReadonlySet<string>,
): Zwischenspalte[] {
  const ergebnis: Zwischenspalte[] = []
  for (const art of ZWISCHENSUMMENARTEN) {
    if (!gewaehlt.has(art)) {
      continue
    }
    let start = 0
    for (let i = 1; i <= perioden.length; i++) {
      if (i === perioden.length || gruppe(art, perioden[i]!) !== gruppe(art, perioden[start]!)) {
        const doppelt = ergebnis.some((z) => z.von === start && z.bis === i - 1)
        if (i - start >= 2 && !doppelt) {
          ergebnis.push({ art, von: start, bis: i - 1 })
        }
        start = i
      }
    }
  }
  return ergebnis
}

/** Was in einer Zwischensumme steht: die Summe — oder bei einem Bestand sein Stand am Rand. */
export function verdichte(werte: number[], z: Zwischenspalte, bestand?: 'anfang' | 'ende' | null): number {
  const teil = werte.slice(z.von, z.bis + 1)
  if (bestand === 'anfang') {
    return teil[0] ?? 0
  }
  if (bestand === 'ende') {
    return teil[teil.length - 1] ?? 0
  }
  return Math.round(teil.reduce((s, w) => s + (w ?? 0), 0) * 100) / 100
}

/** Die Spalten in der Reihenfolge am Bildschirm: jede Periode, dahinter ihre Zwischensummen. */
export type Spaltenplatz = { art: 'periode'; i: number } | { art: 'zwischen'; z: Zwischenspalte }

export function spaltenfolge(anzahl: number, zwischen: Zwischenspalte[]): Spaltenplatz[] {
  const folge: Spaltenplatz[] = []
  for (let i = 0; i < anzahl; i++) {
    folge.push({ art: 'periode', i })
    for (const z of zwischen.filter((z) => z.bis === i)) {
      folge.push({ art: 'zwischen', z })
    }
  }
  return folge
}

/**
 * Der Kopf einer Zwischensumme nennt die Spalten, die sie zusammenfasst —
 * „Jan.–März 2026". Nicht „1. Quartal": Wer Februar bis April gewählt hat,
 * bekommt für das erste Quartal nur Februar und März.
 */
export function zwischenkopf(perioden: number[], z: Zwischenspalte): string {
  const erste = perioden[z.von]!
  const letzte = perioden[z.bis]!
  if (Math.floor(erste / 100) === Math.floor(letzte / 100)) {
    const monat = periodenkuerzel(erste).replace(/\s*\d{4}$/, '')
    return `${monat}–${periodenkuerzel(letzte)}`
  }
  return `${periodenkuerzel(erste)} – ${periodenkuerzel(letzte)}`
}
